import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import QuantityModal from '../components/QuantityModal';
import QRScannerModal from '../components/QRScannerModal';
// (2026-04-17, Jason Cox) R6 UHF hand scanner panel -- feeds EPCs into the
// same addTag path used by manual input and QR scanning.
import RfidScannerPanel from '../components/RfidScannerPanel';
import VerificationModal from '../components/VerificationModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { Product, Category, TagMatch } from '../types';
import { UnifiedProduct } from '../services/unifiedApiService';
import { productService } from '../services/productService';
import { initializeTagService } from '../services/tagService';
import { useAuth } from '../contexts/AuthContext';
import * as Sentry from '@sentry/react';
import { trackButtonClick, addUserActionBreadcrumb, measurePerformance } from '../utils/sentryHelpers';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CubeIcon,
  PlusIcon,
  CameraIcon,
  QrCodeIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';

type WorkflowStep = 'product-selection' | 'tag-input';

interface TagError {
  tag: string;
  error: string;
}

interface TagValidationState {
  tag: string;
  status: 'pending' | 'validating' | 'valid' | 'invalid';
  error?: string;
  productName?: string;
}

const TagItems: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Initialize tagService with user context for API calls
  const tagService = initializeTagService(user);
  
  // Workflow state
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('product-selection');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  
  // Product selector state
  
  // Product selection state
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  
  // Tag input state
  const [tags, setTags] = useState<string[]>([]);
  const [currentTagInput, setCurrentTagInput] = useState('');
  const [tagErrors, setTagErrors] = useState<TagError[]>([]);
  const [isValidatingTags, setIsValidatingTags] = useState(false);
  const [tagValidationStates, setTagValidationStates] = useState<Map<string, TagValidationState>>(new Map());
  const [isPreMatchValidating, setIsPreMatchValidating] = useState(false);
  
  // Matched tag details for the success modal
  const [matchedTagDetails, setMatchedTagDetails] = useState<Array<{
    rfidNumber: string;
    sku: string;
    productName: string;
    quantity: number;
  }>>([]);
  
  // Modal state
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [showQRScannerModal, setShowQRScannerModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState<string>('');
  const [retryAttempt, setRetryAttempt] = useState(0);

  // (2026-04-17, Jason Cox) Product visibility rules for the list:
  //  * If the user has typed a text search term, show ALL matches (active and
  //    inactive) -- they asked for it by typing.
  //  * Otherwise (e.g. browsing by category only), hide inactive products.
  //  * Either way, sort active items above inactive ones so when there are
  //    multiple matches the actives appear on top.
  const displayedProducts = useMemo(() => {
    const hasTextSearch = searchTerm.trim().length > 0;
    const filtered = hasTextSearch
      ? products
      : products.filter(p => p.status !== 'inactive');
    const rank = (p: Product) => (p.status === 'inactive' ? 1 : 0);
    return [...filtered].sort((a, b) => rank(a) - rank(b));
  }, [products, searchTerm]);

  let searchTimeout: NodeJS.Timeout;

  useEffect(() => {
    loadCategories();
    
    // Check if we're coming from Product Details with pre-selected data
    if (location.state?.selectedProduct && location.state?.quantity) {
      setSelectedProduct(location.state.selectedProduct);
      setQuantity(location.state.quantity);
      setCurrentStep('tag-input');
    }
  }, [location.state]);

  const loadCategories = async () => {
    try {
      const categoriesData = await productService.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      setError('Failed to load categories');
    }
  };

  const handleSearch = async (query: string = searchTerm, categoryId: string | null = selectedCategory) => {
    if (!query.trim() && !categoryId) {
      setProducts([]);
      setHasSearched(false);
      return;
    }

    return measurePerformance('Product Search', async () => {
      setIsLoading(true);
      setError(null);
      setHasSearched(true);
      
      addUserActionBreadcrumb('Product search', { query, categoryId });

      try {
        const results = await productService.searchProducts(query, categoryId || undefined);
        setProducts(results);
      } catch (err) {
        setError('Failed to search products');
        setProducts([]);
        Sentry.captureException(err, {
          tags: { component: 'TagItems', operation: 'product_search' },
          extra: { query, categoryId }
        });
      } finally {
        setIsLoading(false);
      }
    });
  };

  const handleCategorySelect = (categoryId: string | null) => {
    setSelectedCategory(categoryId);
    handleSearch(searchTerm, categoryId);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleSearchButtonClick = trackButtonClick('Search Products Button', () => {
    handleSearch(searchTerm, selectedCategory);
  });

  const handleProductSelect = trackButtonClick('Select Product', (product: Product) => {
    if (product.status === 'inactive') {
      setError('Cannot select inactive products for matching. Please choose an active product.');
      return;
    }
    setSelectedProduct(product);
    setShowQuantityModal(true);
  });

  const handleUnifiedProductSelect = (unifiedProduct: UnifiedProduct) => {
    if (unifiedProduct.status === 'inactive') {
      setError('Cannot select inactive products for matching. Please choose an active product.');
      return;
    }
    
    // Convert UnifiedProduct to our Product type
    const product: Product = {
      id: unifiedProduct.id,
      itemNumber: unifiedProduct.sku,
      description: unifiedProduct.name,
      manufacturer: unifiedProduct.manufacturer || 'Unknown',
      model: unifiedProduct.description || '',
      location: 'Various',
      category: unifiedProduct.category,
      specifications: {},
      status: unifiedProduct.status === 'active' ? 'active' : 'inactive',
      tags: []
    };
    
    setSelectedProduct(product);
    setShowQuantityModal(true);
  };

  const handleQuantityConfirm = (selectedQuantity: number) => {
    setQuantity(selectedQuantity);
    setShowQuantityModal(false);
    setCurrentStep('tag-input');
  };

  const handleTagInput = async (tagNumber: string) => {
    const formattedTag = tagService.formatTagNumber(tagNumber);
    
    if (!tagService.validateTagNumber(formattedTag)) {
      return;
    }

    if (tags.includes(formattedTag)) {
      return;
    }

    setTags(prev => [...prev, formattedTag].sort());
    setCurrentTagInput('');
    
    // Validate tag availability
    await validateTag(formattedTag);
  };

  const validateTag = async (tagNumber: string) => {
    // Update validation state to 'validating'
    setTagValidationStates(prev => {
      const newMap = new Map(prev);
      newMap.set(tagNumber, { tag: tagNumber, status: 'validating' });
      return newMap;
    });
    
    try {
      const tagStatus = await tagService.getTagStatus(tagNumber);
      
      if (!tagStatus.available) {
        const errorMsg = 'Already matched';
        
        setTagErrors(prev => [
          ...prev.filter(err => err.tag !== tagNumber),
          { tag: tagNumber, error: errorMsg }
        ]);
        
        setTagValidationStates(prev => {
          const newMap = new Map(prev);
          newMap.set(tagNumber, { 
            tag: tagNumber, 
            status: 'invalid', 
            error: errorMsg
          });
          return newMap;
        });
      } else {
        setTagErrors(prev => prev.filter(err => err.tag !== tagNumber));
        
        setTagValidationStates(prev => {
          const newMap = new Map(prev);
          newMap.set(tagNumber, { tag: tagNumber, status: 'valid' });
          return newMap;
        });
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to validate tag';
      
      setTagErrors(prev => [
        ...prev.filter(err => err.tag !== tagNumber),
        { tag: tagNumber, error: errorMsg }
      ]);
      
      setTagValidationStates(prev => {
        const newMap = new Map(prev);
        newMap.set(tagNumber, { 
          tag: tagNumber, 
          status: 'invalid', 
          error: errorMsg 
        });
        return newMap;
      });
    }
  };
  
  const validateAllTags = async (tagList: string[]) => {
    setIsValidatingTags(true);
    
    try {
      // Validate all tags in parallel
      await Promise.all(tagList.map(tag => validateTag(tag)));
    } finally {
      setIsValidatingTags(false);
    }
  };

  const handleTagInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentTagInput.trim()) {
      handleTagInput(currentTagInput.trim());
    }
  };

  const handleQRScannerTagsAdded = async (qrTags: string[]) => {
    const validTags: string[] = [];
    
    qrTags.forEach(tag => {
      const formattedTag = tagService.formatTagNumber(tag);
      if (!tagService.validateTagNumber(formattedTag) || tags.includes(formattedTag)) {
        return;
      }
      validTags.push(formattedTag);
    });
    
    if (validTags.length > 0) {
      // Add all valid tags to the list
      setTags(prev => [...prev, ...validTags].sort());
      
      // Set all tags as pending validation
      setTagValidationStates(prev => {
        const newMap = new Map(prev);
        validTags.forEach(tag => {
          newMap.set(tag, { tag, status: 'pending' });
        });
        return newMap;
      });
      
      // Validate all tags
      await validateAllTags(validTags);
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
    setTagErrors(prev => prev.filter(err => err.tag !== tagToRemove));
    setTagValidationStates(prev => {
      const newMap = new Map(prev);
      newMap.delete(tagToRemove);
      return newMap;
    });
  };

  const handleMatch = trackButtonClick('Match Tags', async () => {
    if (tags.length === 0 || !selectedProduct) {
      return;
    }
    
    return Sentry.startSpan(
      {
        op: "ui.action",
        name: "Tag Matching Process",
      },
      async (span) => {
        span?.setAttribute("tag_count", tags.length);
        span?.setAttribute("product_id", selectedProduct.id);
        span?.setAttribute("product_sku", selectedProduct.itemNumber);
        
        addUserActionBreadcrumb('Starting tag match', { 
          tagCount: tags.length,
          productId: selectedProduct.id,
          productSku: selectedProduct.itemNumber
        });
        
        // Pre-match validation of all tags
        setIsPreMatchValidating(true);
        setError(null);
        
        try {
          // Clear any stale errors
          setTagErrors([]);
          
          // Validate all tags before proceeding
          await validateAllTags(tags);
          
          // Check if any tags are invalid after validation
          const hasInvalidTags = Array.from(tagValidationStates.values())
            .some(state => state.status === 'invalid');
          
          if (hasInvalidTags) {
        setError('Some tags are invalid. Please remove them before matching.');
        return;
      }
      
          // All tags are valid, proceed to verification
          setShowVerificationModal(true);
        } catch (err) {
          console.error('Pre-match validation error:', err);
          setError('Failed to validate tags. Please try again.');
          span?.setStatus({ code: 2 }); // Error status
          Sentry.captureException(err, {
            tags: { component: 'TagItems', operation: 'pre_match_validation' },
            extra: { tags, productId: selectedProduct?.id }
          });
        } finally {
          setIsPreMatchValidating(false);
        }
      }
    );
  });

  const handleVerificationConfirm = async () => {
    if (!selectedProduct || !user) return;
    
    // Check internet connection first
    if (!navigator.onLine) {
      setError('No internet connection. Please check your connection and try again.');
      setShowVerificationModal(false);
      return;
    }
    
    setIsProcessing(true);
    setProcessingMessage('Creating tag matches...');
    setRetryAttempt(0);
    
    try {
      // Create tag matches for each tag, handling individual failures
      const results = await Promise.allSettled(
        tags.map(async (tag, index) => {
          // Update progress message for each tag
          setProcessingMessage(`Processing tag ${index + 1} of ${tags.length}...`);
          
          try {
            const result = await tagService.createTagMatch(selectedProduct.id, tag, quantity, user.id);
            
            // The tagService now includes verification, so if we get here, it's verified
            setProcessingMessage(`Verified tag ${index + 1} of ${tags.length} ✓`);
            return result;
          } catch (error) {
            // Check if it's a retry message
            if (error instanceof Error && error.message.includes('Retrying')) {
              const attemptMatch = error.message.match(/attempt (\d+)\/(\d+)/);
              if (attemptMatch) {
                setRetryAttempt(parseInt(attemptMatch[1]));
                setProcessingMessage(`Retrying tag ${tag} (attempt ${attemptMatch[1]}/${attemptMatch[2]})...`);
              }
            }
            throw error;
          }
        })
      );
      
      // Process results and collect errors
      const newTagErrors: TagError[] = [];
      const successfulTags: string[] = [];
      const successfulTagDetails: Array<{
        rfidNumber: string;
        sku: string;
        productName: string;
        quantity: number;
      }> = [];
      
      results.forEach((result, index) => {
        const tag = tags[index];
        
        if (result.status === 'rejected') {
          // Extract error message
          const errorMessage = result.reason?.message || 'Failed to create tag match';
          
          // Provide clearer error messages
          let userFriendlyError = errorMessage;
          if (errorMessage.includes('timeout')) {
            userFriendlyError = 'Connection timeout - please check your internet';
          } else if (errorMessage.includes('No internet')) {
            userFriendlyError = 'No internet connection';
          } else if (errorMessage.includes('verification failed')) {
            userFriendlyError = 'Tag created but verification failed - please refresh';
          }
          
          newTagErrors.push({ tag, error: userFriendlyError });
        } else {
          successfulTags.push(tag);
          
          // Collect successful tag details for the modal
          successfulTagDetails.push({
            rfidNumber: tag,
            sku: selectedProduct.itemNumber,
            productName: selectedProduct.description,
            quantity: quantity
          });
        }
      });
      
      // Remove successfully matched tags from the list
      if (successfulTags.length > 0) {
        setTags(prev => prev.filter(tag => !successfulTags.includes(tag)));
        
        // Clear validation states for successful tags
        setTagValidationStates(prev => {
          const newMap = new Map(prev);
          successfulTags.forEach(tag => newMap.delete(tag));
          return newMap;
        });
      }
      
      // Update tag errors state for failed tags
      setTagErrors(newTagErrors);
      
      // Update validation states for failed tags
      if (newTagErrors.length > 0) {
        setTagValidationStates(prev => {
          const newMap = new Map(prev);
          newTagErrors.forEach(({ tag, error }) => {
            newMap.set(tag, { tag, status: 'invalid', error });
          });
          return newMap;
        });
      }
      
      if (newTagErrors.length === 0) {
        // All tags succeeded and verified
        setProcessingMessage('All tags verified successfully!');
        setMatchedTagDetails(successfulTagDetails);
        
        // Clear all tags since they all succeeded
        setTags([]);
        setTagValidationStates(new Map());
        
        // Brief delay to show success message
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setShowVerificationModal(false);
        setShowConfirmationModal(true);
      } else {
        // Some or all failed - close modal so user can see individual tag errors
        setShowVerificationModal(false);
        if (successfulTags.length > 0) {
          // Some succeeded - show success count and remaining errors
          setMatchedTagDetails(successfulTagDetails);
          setError(`✅ ${successfulTags.length} tag(s) matched successfully. ❌ ${newTagErrors.length} tag(s) failed - please remove them and try again.`);
        } else {
          setError('All tag matches failed. Please check individual tag errors below and remove invalid tags.');
        }
      }
    } catch (err) {
      console.error('Unexpected error in tag matching:', err);
      setError(err instanceof Error ? err.message : 'Failed to create tag matches');
      setShowVerificationModal(false);
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
      setRetryAttempt(0);
    }
  };

  const handleTagMore = () => {
    // Reset to initial state
    setCurrentStep('product-selection');
    setSelectedProduct(null);
    setQuantity(1);
    setTags([]);
    setTagErrors([]);
    setTagValidationStates(new Map());
    setMatchedTagDetails([]);
    setCurrentTagInput('');
    setProducts([]);
    setHasSearched(false);
    setSearchTerm('');
    setSelectedCategory(null);
    setShowConfirmationModal(false);
  };

  const handleViewTags = () => {
    navigate('/manage-tags', { state: { recentlyMatchedTags: tags } });
  };


  const getSelectedCategoryName = () => {
    if (!selectedCategory) return null;
    return categories.find(cat => cat.id === selectedCategory)?.name || null;
  };

  // Check if all tags are validated and none are invalid
  const allTagsValidated = tags.every(tag => {
    const state = tagValidationStates.get(tag);
    return state && (state.status === 'valid' || state.status === 'invalid');
  });
  
  const hasInvalidTags = Array.from(tagValidationStates.values())
    .some(state => state.status === 'invalid');
  
  const canMatch = tags.length > 0 && !hasInvalidTags && allTagsValidated && !isValidatingTags && !isPreMatchValidating;

  return (
    <Layout>
      {currentStep === 'product-selection' ? (
        <div className="flex h-screen bg-secondary-50">
          {/* Mobile Categories Overlay */}
          {isCategoriesOpen && (
            <div 
              className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
              onClick={() => setIsCategoriesOpen(false)}
            />
          )}
          
          {/* Categories Sidebar */}
          <div className={`
            fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-secondary-200 flex flex-col transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:z-auto
            ${
              isCategoriesOpen ? 'translate-x-0' : '-translate-x-full'
            }
          `}>
            <div className="p-4 border-b border-secondary-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-secondary-900">Categories</h2>
              <button
                onClick={() => setIsCategoriesOpen(false)}
                className="lg:hidden p-1 rounded-md text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <div className="p-2">
                <button
                  onClick={() => {
                    handleCategorySelect(null);
                    setIsCategoriesOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedCategory === null
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-secondary-700 hover:bg-secondary-100 hover:text-secondary-900'
                  }`}
                >
                  All Categories
                </button>
                
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      handleCategorySelect(category.id);
                      setIsCategoriesOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors mt-1 ${
                      selectedCategory === category.id
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-secondary-700 hover:bg-secondary-100 hover:text-secondary-900'
                    }`}
                  >
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="truncate">{category.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-secondary-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <button
                    onClick={() => setIsCategoriesOpen(true)}
                    className="lg:hidden flex items-center px-3 py-2 rounded-md text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 mr-3 border border-secondary-300"
                  >
                    <FunnelIcon className="h-4 w-4 mr-2" />
                    <span className="text-sm font-medium">
                      {selectedCategory ? getSelectedCategoryName() : 'Categories'}
                    </span>
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                  </button>
                  <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Tag Items</h1>
                  </div>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white border-b border-secondary-200 p-4">
              <div className="flex max-w-md gap-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    onKeyDown={(e) => e.key === 'Enter' && (searchTerm.trim().length >= 3 || selectedCategory) && handleSearchButtonClick()}
                    placeholder="Search products (min. 3 characters)..."
                    className="block w-full pl-10 pr-10 py-2 border border-secondary-300 rounded-md leading-5 bg-white placeholder-secondary-500 focus:outline-none focus:placeholder-secondary-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedCategory(null);
                        setProducts([]);
                        setHasSearched(false);
                      }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <XMarkIcon className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                    </button>
                  )}
                </div>
                <button
                  onClick={handleSearchButtonClick}
                  disabled={(searchTerm.trim().length > 0 && searchTerm.trim().length < 3) || (!searchTerm.trim() && !selectedCategory)}
                  className={`px-4 py-2 font-medium text-sm rounded-md transition-colors ${
                    (searchTerm.trim().length > 0 && searchTerm.trim().length < 3) || (!searchTerm.trim() && !selectedCategory)
                      ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                  }`}
                >
                  Search
                </button>
              </div>
              
              {searchTerm.trim().length > 0 && searchTerm.trim().length < 3 && (
                <div className="mt-2 text-sm text-secondary-500">
                  Please enter at least 3 characters to search
                </div>
              )}
              
              {/* Active Filters */}
              {(selectedCategory || searchTerm) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedCategory && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      {getSelectedCategoryName()}
                      <button
                        onClick={() => handleCategorySelect(null)}
                        className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-primary-400 hover:text-primary-600"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                  {searchTerm && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                      Search: "{searchTerm}"
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Products List */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Legacy product search - keeping for fallback */}
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <LoadingSpinner size="lg"/>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-error-500 text-sm">{error}</div>
                </div>
              ) : !hasSearched ? (
                <div className="text-center py-12">
                  <CubeIcon className="mx-auto h-12 w-12 text-secondary-400" />
                  <h3 className="mt-2 text-sm font-medium text-secondary-900">Select a product to tag</h3>
                  <p className="mt-1 text-sm text-secondary-500">
                    Search for products by name or number, or select a category to get started.
                  </p>
                </div>
              ) : displayedProducts.length === 0 ? (
                <div className="text-center py-12">
                  <CubeIcon className="mx-auto h-12 w-12 text-secondary-400" />
                  <h3 className="mt-2 text-sm font-medium text-secondary-900">No products found</h3>
                  <p className="mt-1 text-sm text-secondary-500">
                    {/* (2026-04-17, Jason Cox) If we hid inactive items and that
                        left nothing, hint that typing a search term will reveal them. */}
                    {products.length > 0 && searchTerm.trim().length === 0
                      ? 'All matches are inactive. Type a search term to include inactive items.'
                      : 'Try adjusting your search terms or selecting a different category.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-secondary-900">Search Results</h3>
                    <span className="text-sm text-secondary-600">
                      {/* (2026-04-17, Jason Cox) Show displayed count; if we hid
                          any inactive items, note the delta in parentheses. */}
                      {displayedProducts.length} product{displayedProducts.length !== 1 ? 's' : ''} found
                      {products.length > displayedProducts.length && (
                        <> <span className="text-secondary-400">({products.length - displayedProducts.length} inactive hidden)</span></>
                      )}
                    </span>
                  </div>
                  <div className="grid gap-4">
                    {/* (2026-04-17, Jason Cox) Renders displayedProducts:
                        hides inactive items when there's no text search term,
                        and sorts active-first when multiple matches are shown. */}
                    {displayedProducts.map((product) => {
                      const category = categories.find(cat => cat.id === product.category);
                      return (
                        <button
                          key={product.id}
                          onClick={() => handleProductSelect(product)}
                          disabled={product.status === 'inactive'}
                          className={`block w-full rounded-lg border transition-all text-left ${
                            product.status === 'inactive' 
                              ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-75' 
                              : 'bg-white border-secondary-200 hover:shadow-md hover:border-primary-300'
                          }`}
                        >
                          <div className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center">
                                  <h3 className={`text-sm font-medium ${
                                    product.status === 'inactive' ? 'text-gray-500' : 'text-secondary-900'
                                  }`}>
                                    {product.itemNumber}
                                  </h3>
                                  {category && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-800">
                                      <div 
                                        className="w-2 h-2 rounded-full mr-1"
                                        style={{ backgroundColor: category.color }}
                                      />
                                      {category.name}
                                    </span>
                                  )}
                                  <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    product.status === 'active' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {product.status === 'active' ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                                <p className="mt-1 text-sm text-secondary-600">
                                  {product.description}
                                </p>
                                <div className="mt-2 flex items-center text-xs text-secondary-500">
                                  {Object.entries(product.specifications).map(([key, value]) => (
                                    <span key={key}>{key}: {value}</span>
                                  ))}
                                </div>
                              </div>
                              <div className="ml-4 flex-shrink-0">
                                <PlusIcon className="h-5 w-5 text-primary-600" />
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Tag Input Step */
        <div className="py-6">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-6">
              <button
                onClick={() => setCurrentStep('product-selection')}
                className="inline-flex items-center text-sm font-medium text-secondary-600 hover:text-secondary-900 mb-4"
              >
                ← Back to Product Selection
              </button>
              
              <h1 className="text-2xl font-bold text-secondary-900">
                Match RFID Tags
              </h1>
              <p className="mt-1 text-sm text-secondary-600">
                Add RFID tags to associate with the selected product
              </p>
            </div>

            {/* Product Info */}
            {selectedProduct && (
              <div className="card mb-6">
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-secondary-900">
                        {selectedProduct.itemNumber}
                      </h3>
                      <p className="text-sm text-secondary-600">
                        {selectedProduct.description}
                      </p>
                      <p className="text-sm text-secondary-500 mt-1">
                        Package Quantity: {quantity} item{quantity !== 1 ? 's' : ''} per tag
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-secondary-500">
                        {selectedProduct.manufacturer} • {selectedProduct.model}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tag Input Methods (2026-04-22 / 2026-04-24 / 2026-04-30):
                Scanner + Camera stay side-by-side at every width so it's
                visually clear they are two alternative inputs. The shared
                intro covers both -- the hand scanner and the camera both
                read the QR / barcode labels on the items. Manual Entry
                sits below as the keyboard fallback. */}
            <p className="text-xs text-secondary-600 mb-2">
              Scan a QR or barcode label with the RFID hand scanner or your device camera. Multiple tags can be scanned in one session.
            </p>
            <div className="grid grid-cols-2 gap-3 md:gap-6 mb-6">
              <RfidScannerPanel
                title="Hand Scanner"
                onTagScanned={(epc) => handleTagInput(epc)}
              />

              <div className="card">
                <div className="card-body">
                  <h3 className="text-sm font-semibold text-secondary-900 mb-2">Camera</h3>
                  <button
                    type="button"
                    onClick={() => setShowQRScannerModal(true)}
                    className="btn-primary w-full flex flex-col items-center justify-center whitespace-nowrap"
                  >
                    <CameraIcon className="h-5 w-5 mb-1" />
                    <span>Start</span>
                    <span>Camera</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Manual Entry -- keyboard fallback, below the scanner/camera. */}
            <div className="card mb-6">
              <div className="card-header">
                <h3 className="text-lg font-medium text-secondary-900">Manual Entry</h3>
              </div>
              <div className="card-body">
                <form onSubmit={handleTagInputSubmit}>
                  <div className="space-y-3">
                    <div className="flex space-x-2">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={currentTagInput}
                          onChange={(e) => setCurrentTagInput(e.target.value.toUpperCase())}
                          placeholder="Enter 12-character tag number"
                          className={`w-full input ${
                            currentTagInput.trim() && (!tagService.validateTagNumber(currentTagInput) || tags.includes(tagService.formatTagNumber(currentTagInput)))
                              ? 'border-error-300 focus:border-error-500 focus:ring-error-500'
                              : ''
                          }`}
                          maxLength={12}
                        />
                        {currentTagInput.trim() && !tagService.validateTagNumber(currentTagInput) && (
                          <div className="mt-1 flex items-center text-error-600">
                            <ExclamationTriangleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span className="text-sm">Tag must be exactly 12 characters</span>
                          </div>
                        )}
                        {currentTagInput.trim() && tagService.validateTagNumber(currentTagInput) && tags.includes(tagService.formatTagNumber(currentTagInput)) && (
                          <div className="mt-1 flex items-center text-error-600">
                            <ExclamationTriangleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span className="text-sm">This tag has already been added to the list</span>
                          </div>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={!currentTagInput.trim() || !tagService.validateTagNumber(currentTagInput) || tags.includes(tagService.formatTagNumber(currentTagInput))}
                        className={`px-4 py-2 font-medium text-sm rounded-md transition-colors ${
                          !currentTagInput.trim() || !tagService.validateTagNumber(currentTagInput) || tags.includes(tagService.formatTagNumber(currentTagInput))
                            ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed'
                            : 'bg-success-600 text-white hover:bg-success-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-success-500'
                        }`}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* Tag List */}
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h3 className="text-lg font-medium text-secondary-900">Added Tags</h3>
                {hasInvalidTags && (
                  <button
                    onClick={() => {
                      // Remove all invalid tags
                      const invalidTags = Array.from(tagValidationStates.entries())
                        .filter(([_, state]) => state.status === 'invalid')
                        .map(([tag, _]) => tag);
                      
                      setTags(prev => prev.filter(tag => !invalidTags.includes(tag)));
                      setTagErrors([]);
                      setTagValidationStates(prev => {
                        const newMap = new Map(prev);
                        invalidTags.forEach(tag => newMap.delete(tag));
                        return newMap;
                      });
                    }}
                    className="text-sm text-error-600 hover:text-error-700 font-medium"
                  >
                    Remove Invalid Tags
                  </button>
                )}
              </div>
              <div className="card-body">
                {tags.length === 0 ? (
                  <div className="text-center py-8">
                    <QrCodeIcon className="mx-auto h-12 w-12 text-secondary-400" />
                    <h3 className="mt-2 text-sm font-medium text-secondary-900">No tags added</h3>
                    <p className="mt-1 text-sm text-secondary-500">
                      Add RFID tags using manual entry or camera scanning
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {tags.sort().map((tag) => {
                      const validationState = tagValidationStates.get(tag);
                      const error = tagErrors.find(err => err.tag === tag);
                      
                      // Determine background and border colors based on validation state
                      let bgColor = 'bg-secondary-50';
                      let borderColor = 'border-secondary-200';
                      let statusIcon = null;
                      
                      if (validationState) {
                        switch (validationState.status) {
                          case 'validating':
                            bgColor = 'bg-blue-50';
                            borderColor = 'border-blue-300';
                            statusIcon = (
                              <svg className="animate-spin h-4 w-4 text-blue-600 ml-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            );
                            break;
                          case 'valid':
                            bgColor = 'bg-success-50';
                            borderColor = 'border-success-300';
                            statusIcon = (
                              <svg className="h-4 w-4 text-success-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            );
                            break;
                          case 'invalid':
                            bgColor = 'bg-error-50';
                            borderColor = 'border-error-300';
                            statusIcon = (
                              <ExclamationTriangleIcon className="h-4 w-4 text-error-600 ml-2" />
                            );
                            break;
                          case 'pending':
                            bgColor = 'bg-gray-50';
                            borderColor = 'border-gray-300';
                            break;
                        }
                      }
                      
                      return (
                        <div key={tag} className={`flex items-center justify-between p-3 ${bgColor} rounded-lg border ${borderColor}`}>
                          <div className="flex items-center flex-1">
                            <span className="text-sm font-mono text-secondary-900">{tag}</span>
                            {statusIcon}
                            {error && (
                              <div className="ml-3 flex items-center text-error-600 flex-1">
                                <span className="text-sm">{error.error}</span>
                              </div>
                            )}
                            {validationState?.status === 'validating' && (
                              <span className="ml-3 text-sm text-blue-600">Validating...</span>
                            )}
                          </div>
                          <button
                            onClick={() => removeTag(tag)}
                            className="text-secondary-400 hover:text-secondary-600 ml-2"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Match Button */}
            {tags.length > 0 && (
              <div className="mt-6">
                {hasInvalidTags && (
                  <div className="mb-3 p-3 bg-error-50 border border-error-300 rounded-lg">
                    <div className="flex items-center text-error-700">
                      <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                      <span className="text-sm font-medium">
                        Remove invalid tags before matching
                      </span>
                    </div>
                  </div>
                )}
                {!allTagsValidated && !hasInvalidTags && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-300 rounded-lg">
                    <div className="flex items-center text-blue-700">
                      <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm font-medium">
                        Validating tags...
                      </span>
                    </div>
                  </div>
                )}
                <div className="flex justify-end">
                  <button
                    onClick={handleMatch}
                    disabled={!canMatch}
                    className={`btn-primary px-6 py-3 ${!canMatch ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isPreMatchValidating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Validating...
                      </>
                    ) : (
                      `Match ${tags.length} Tag${tags.length !== 1 ? 's' : ''}`
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedProduct && (
        <>
          <QuantityModal
            isOpen={showQuantityModal}
            onClose={() => setShowQuantityModal(false)}
            onConfirm={handleQuantityConfirm}
            productName={`${selectedProduct.itemNumber} - ${selectedProduct.description}`}
          />

          <VerificationModal
            isOpen={showVerificationModal}
            onClose={() => setShowVerificationModal(false)}
            onConfirm={handleVerificationConfirm}
            product={selectedProduct}
            quantity={quantity}
            tags={tags}
            isLoading={isProcessing}
            processingMessage={processingMessage}
          />
        </>
      )}

      <QRScannerModal
        isOpen={showQRScannerModal}
        onClose={() => setShowQRScannerModal(false)}
        onTagsAdded={handleQRScannerTagsAdded}
      />

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onTagMore={handleTagMore}
        onViewTags={handleViewTags}
        matchedTagsCount={matchedTagDetails.length}
        productName={selectedProduct ? `${selectedProduct.itemNumber} - ${selectedProduct.description}` : ''}
        matchedTags={matchedTagDetails}
      />

    </Layout>
  );
};

export default TagItems;